import classnames from 'classnames'
import * as React from 'react'

import { BoxProps } from '../../../main/components/box'
import { ButtonHTMLProps, ButtonProps } from '../../../main/components/button'
import { Select, SelectOption, SelectProps } from '../../../main/components/select'
import { SelectItem } from '../../../main/components/select/types'

export enum MapSelectTheme {
  light,
  dark,
}

interface MapSelectThemeReturn {
  label: React.HTMLProps<HTMLHeadingElement>
  button: ButtonProps & ButtonHTMLProps
  popover: BoxProps
}

function getMapSelectTheme(theme: MapSelectTheme): MapSelectThemeReturn | undefined {
  switch (theme) {
    case MapSelectTheme.dark:
      return {
        label: {
          className: 'text-gray-400',
        },
        button: {
          theme: 'transparent',
          rounding: 'none',
          className: 'bg-[#4a505f] border border-[#585f72] hover:bg-[#535a6b] hover:border-[#707990]',
          contentClass: 'truncate',
          rightContent: 'caretDown',
        },
        popover: {
          rounding: 'none',
          theme: 'none',
          className: 'overflow-y-auto max-h-80 bg-[#4a505f] border border-[#585f72]',
        },
      }

    case MapSelectTheme.light:
      return {
        label: { className: 'text-gray-500' },
        button: { rounding: 'none', rightContent: 'caretDown' },
        popover: { rounding: 'none', className: 'overflow-y-auto max-h-80' },
      }
  }
}

interface MapSelectProps<ItemType, ValueType, Name> extends Omit<SelectProps<ItemType, ValueType, Name>, 'theme'> {
  /**
   * Theme for the map select
   *
   * @default dark
   */
  theme?: keyof typeof MapSelectTheme
}

export function MapSelect<ValueType extends React.Key, ItemType extends SelectItem<ValueType>, Name = never>({
  label,
  'aria-label': ariaLabel,
  theme = 'dark',
  ...props
}: MapSelectProps<ItemType, ValueType, Name>) {
  const selectTheme = React.useMemo(() => getMapSelectTheme(MapSelectTheme[theme]), [theme])

  return (
    <Select
      size="medium"
      label={
        label && (
          <h3 className={classnames(selectTheme?.label.className, 'm-0 text-3xs uppercase font-bold')}>{label}</h3>
        )
      }
      aria-label={ariaLabel ?? (typeof label === 'string' ? label : undefined)}
      className="w-full"
      placeholder="Any"
      listProps={selectTheme?.popover}
      {...selectTheme?.button}
      {...props}
      onChange={props.onChange}
      name={props.name}
      hasPopoverButtonWidthLimit={false}
    >
      {props.children ??
        props.items.map((subtype) => (
          <SelectOption key={subtype.value} rounding="none" size="medium">
            {subtype.label}
          </SelectOption>
        ))}
    </Select>
  )
}
